import { useCallback, useEffect, useRef, useState } from 'react'

import { AxiosError } from 'axios'

import { useToast } from '@pnld/components-web'

import { IErrorDTO } from '@/DTOs/errorDTO'
import { TReportJobStatus } from '@/types/report'
import { getErrorDetails } from '@/utils/error'

import {
  TDownloadState,
  TUseReportDownloadOptions,
  TUseReportDownloadReturn,
} from './types'

const INITIAL_DOWNLOAD_STATE: TDownloadState = {
  jobId: null,
  status: 'idle',
  progressPct: 0,
  progressRows: 0,
  totalRows: 0,
  error: null,
}

const DEFAULT_POLL_INTERVAL_MS = 3000
// PSV-461: o acompanhamento não tem mais prazo total — a memória de cálculo
// do PNLD 2027 leva ~30 min em hmg, e o prazo fixo de 15 min derrubava a tela
// com a tarefa viva. Desiste só quando a tarefa para de dar sinal de vida.
// A margem cobre o xlsx parado perto de 100% durante a gravação final e o CSV,
// que grava o progresso a cada 50 mil linhas.
const DEFAULT_STALL_TIMEOUT_MS = 15 * 60 * 1000
const DEFAULT_PENDING_TIMEOUT_MS = 5 * 60 * 1000
// Por tempo, e não por contagem: um deploy ou um 502 do ingress de alguns
// minutos não pode encerrar o acompanhamento.
const DEFAULT_ERROR_TIMEOUT_MS = 5 * 60 * 1000
const MAX_RETRY_DELAY_MS = 30 * 1000

const RESUME_HINT =
  'A geração pode continuar no servidor: clique em exportar de novo para retomar o acompanhamento.'

const triggerDownload = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const isNotFound = (err: unknown) =>
  (err as AxiosError | undefined)?.response?.status === 404

/**
 * Fluxo de download assíncrono de relatório (job + polling + multi-partes),
 * extraído de Predictions/Results.tsx para reuso em outros módulos.
 *
 * O polling é um `setTimeout` encadeado: a próxima consulta só é agendada
 * depois que a anterior responde, então duas nunca se sobrepõem. Ele desiste
 * em quatro casos, cada um com a sua mensagem:
 *  - o job sumiu (404);
 *  - as consultas falharam sem parar por `errorTimeoutMs` (falhas de rede ou
 *    de gateway são tentadas de novo, com espera crescente);
 *  - o job ficou em PENDING por `pendingTimeoutMs` (worker fora do ar);
 *  - o progresso não avançou por `stallTimeoutMs` (worker morto ou travado).
 */
const useReportDownload = ({
  request,
  getStatus,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
  pendingTimeoutMs = DEFAULT_PENDING_TIMEOUT_MS,
  errorTimeoutMs = DEFAULT_ERROR_TIMEOUT_MS,
}: TUseReportDownloadOptions): TUseReportDownloadReturn => {
  const toast = useToast()

  const [state, setState] = useState<TDownloadState>(INITIAL_DOWNLOAD_STATE)

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Job acompanhado agora. Uma resposta que chega depois de parar (desmontou,
  // terminou) é descartada por aqui, e não pelo timer, que já disparou.
  const activeJobRef = useRef<string | null>(null)
  // Pedido de job em voo: segura o segundo clique antes de o job existir.
  const startingRef = useRef(false)
  const mountedRef = useRef(true)
  const downloadTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
    activeJobRef.current = null
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollingRef.current) clearTimeout(pollingRef.current)
      activeJobRef.current = null
      downloadTimeoutsRef.current.forEach(clearTimeout)
    }
  }, [])

  const giveUp = useCallback(
    (title: string, description: string) => {
      stopPolling()
      setState(prev => ({ ...prev, status: 'failed' }))
      toast({ status: 'error', title, description, isClosable: true })
    },
    [stopPolling, toast]
  )

  const startPolling = useCallback(
    (jobId: string) => {
      activeJobRef.current = jobId

      let lastProgressRows: number | null = null
      let lastProgressAt = Date.now()
      let pendingSince = Date.now()
      let wasPending = true
      let consecutiveErrors = 0
      let failingSince: number | null = null

      const schedule = (delay: number) => {
        pollingRef.current = setTimeout(poll, delay)
      }

      async function poll() {
        pollingRef.current = null
        let status: TReportJobStatus
        try {
          status = await getStatus(jobId)
        } catch (err) {
          if (activeJobRef.current !== jobId) return
          consecutiveErrors += 1
          failingSince = failingSince ?? Date.now()
          if (isNotFound(err) || Date.now() - failingSince > errorTimeoutMs) {
            giveUp(
              'Erro ao consultar status',
              `${getErrorDetails(err as AxiosError<IErrorDTO>)} ${
                isNotFound(err) ? '' : RESUME_HINT
              }`.trim()
            )
            return
          }
          schedule(
            Math.min(
              pollIntervalMs * 2 ** (consecutiveErrors - 1),
              MAX_RETRY_DELAY_MS
            )
          )
          return
        }
        if (activeJobRef.current !== jobId) return
        consecutiveErrors = 0
        failingSince = null

        setState(prev => ({
          ...prev,
          status: status.status.toLowerCase() as TDownloadState['status'],
          progressPct: status.progressPct,
          progressRows: status.progressRows,
          totalRows: status.totalRows,
          error: status.error || null,
        }))

        if (status.status === 'DONE') {
          stopPolling()

          const isMultiPart = (status.totalParts ?? 1) > 1

          if (isMultiPart) {
            status.downloadUrls.forEach((url, i) => {
              downloadTimeoutsRef.current.push(
                setTimeout(() => triggerDownload(url), i * 1000)
              )
            })
            toast({
              status: 'info',
              title: `Relatório dividido em ${status.totalParts} arquivos`,
              description:
                'O relatório excedeu o limite do Excel e foi dividido. Os downloads iniciarão em sequência.',
              isClosable: true,
              duration: 8000,
              position: 'top',
            })
          } else {
            triggerDownload(status.downloadUrl)
          }

          toast({
            status: 'success',
            title: 'Exportação concluída com sucesso!',
            isClosable: true,
          })
          return
        }

        if (status.status === 'FAILED') {
          stopPolling()
          toast({
            status: 'error',
            title: 'Erro ao gerar relatório',
            description: status.error || 'Tente novamente.',
            isClosable: true,
          })
          return
        }

        const now = Date.now()

        if (status.status === 'PENDING') {
          if (now - pendingSince > pendingTimeoutMs) {
            giveUp(
              'O relatório não começou a ser gerado',
              'O serviço de geração não respondeu. Tente novamente em alguns minutos.'
            )
            return
          }
          wasPending = true
        } else {
          pendingSince = now
          // Qualquer mudança é sinal de vida, inclusive voltar a zero (o job
          // recomeçou); e o relógio só começa ao sair de PENDING.
          if (wasPending || status.progressRows !== lastProgressRows) {
            lastProgressRows = status.progressRows
            lastProgressAt = now
            wasPending = false
          } else if (now - lastProgressAt > stallTimeoutMs) {
            giveUp('A geração parou de avançar', RESUME_HINT)
            return
          }
        }

        schedule(pollIntervalMs)
      }

      schedule(pollIntervalMs)
    },
    [
      errorTimeoutMs,
      getStatus,
      giveUp,
      pendingTimeoutMs,
      pollIntervalMs,
      stallTimeoutMs,
      stopPolling,
      toast,
    ]
  )

  const start = useCallback(async () => {
    if (activeJobRef.current || startingRef.current) return
    startingRef.current = true

    setState({ ...INITIAL_DOWNLOAD_STATE, status: 'pending' })

    try {
      const { jobId } = await request()
      // A tela fechou antes de o job existir: sem quem mostre o progresso,
      // acompanhar só dispararia toasts e um download que ninguém pediu ali.
      if (!mountedRef.current) return

      setState(prev => ({ ...prev, jobId, status: 'processing' }))
      startPolling(jobId)

      toast({
        status: 'info',
        title: 'Relatório em geração',
        description:
          'O arquivo está sendo processado em segundo plano. Você será notificado quando estiver pronto.',
        isClosable: true,
        duration: 6000,
        position: 'top',
      })
    } catch (err) {
      if (!mountedRef.current) return
      setState(INITIAL_DOWNLOAD_STATE)
      toast({
        status: 'error',
        title: 'Erro ao solicitar relatório',
        description: getErrorDetails(err as AxiosError<IErrorDTO>),
        isClosable: true,
      })
    } finally {
      startingRef.current = false
    }
  }, [request, startPolling, toast])

  const isLoading = state.status === 'pending' || state.status === 'processing'

  return { state, start, isLoading }
}

export default useReportDownload
